/* eslint-disable perfectionist/sort-imports */
import "src/global.css";

// i18n
import "src/locales/i18n";

// ----------------------------------------------------------------------

import Router from "src/routes/sections";

import { useScrollToTop } from "src/hooks/use-scroll-to-top";

import ThemeProvider from "src/theme";
import { LocalizationProvider } from "src/locales";

import ProgressBar from "src/components/progress-bar";
import { MotionLazy } from "src/components/animate/motion-lazy";
import SnackbarProvider from "src/components/snackbar/snackbar-provider";
import { SettingsDrawer, SettingsProvider } from "src/components/settings";

import { CheckoutProvider } from "src/sections/checkout/context";

import { AuthProvider } from "src/auth/context/jwt";
import { TransactionProvider } from "./sections/checkout/context/transaction-context";
import VersionCheckSnackbar from "./components/version-check-snackbar/version-check-snackbar";

import { ConsentProvider } from "./components/consent/ConsentProvider";
import CookieBanner from "./components/consent/CookieBanner";
import CookiePreferences from "./components/consent/CookiePreferences";
import { Alert, Box, Link } from "@mui/material";
import PlayerHelpDialog from "./layouts/common/player-help-dialog";
import { PLATFORM_ENV } from "src/config-global";
import { useState } from "react";
import AppErrorBoundary from "src/components/error-handling/AppErrorBoundary";
import GlobalErrorListeners from "src/components/error-handling/GlobalErrorListeners";
import LanguagePopover from "./layouts/common/language-popover";

function PlayerHelperIcon() {
  return (
    <Box sx={{ position: "fixed", left: 0, bottom: 20, zIndex: 1299 }}>
      <PlayerHelpDialog
        section={"player_help_section_start_steps"}
        sx={{ paddingTop: 0, paddingRight: 0, transform: "scale(1.6)" }}
      />
    </Box>
  );
}

function LanguagePopoverIcon() {
  const isDashboard = window.location.href.includes("dashboard");
  if (isDashboard) return;
  return (
    <Box sx={{ position: "fixed", left: 0, bottom: 50, zIndex: 1299 }}>
      <LanguagePopover />
    </Box>
  );
}

function TestBanner() {
  const [showTestBanner, setShowTestBanner] = useState(PLATFORM_ENV === "TEST");
  return (
    showTestBanner && (
      <div
        style={{
          position: "fixed",
          bottom: "50%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          width: "90%",
          maxWidth: 800,
        }}
      >
        <Alert
          severity="error"
          sx={{ fontSize: 30 }}
          onClose={() => setShowTestBanner(false)}
        >
          You are using the TEST environment.
          <br />
          Live at: <Link href="https://foggosport.com">FoggoSport.com</Link>
        </Alert>
      </div>
    )
  );
}

// import { AuthProvider } from 'src/auth/context/auth0';
// import { AuthProvider } from 'src/auth/context/amplify';
// import { AuthProvider } from 'src/auth/context/firebase';

// ----------------------------------------------------------------------

export default function App() {
  useScrollToTop();
  const showTestBanner = PLATFORM_ENV === "TEST";

  return (
    <AppErrorBoundary>
      <ConsentProvider>
        <GlobalErrorListeners />
        {/* Global TEST environment banner */}
        <TestBanner />
        {/* Consent UI */}
        <CookieBanner />
        <CookiePreferences />

        {/* Trackers (load only if consented) */}

        <AuthProvider>
          <LocalizationProvider>
            <SettingsProvider
              defaultSettings={{
                themeMode: "light", // 'light' | 'dark'
                themeDirection: "ltr", //  'rtl' | 'ltr'
                themeContrast: "default", // 'default' | 'bold'
                themeLayout: "vertical", // 'vertical' | 'horizontal' | 'mini'
                themeColorPresets: "default", // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
                themeStretch: false,
              }}
            >
              <ThemeProvider>
                <MotionLazy>
                  <SnackbarProvider>
                    <TransactionProvider>
                      <CheckoutProvider>
                        <SettingsDrawer />
                        <ProgressBar />
                        <VersionCheckSnackbar />

                        <Router />
                        <PlayerHelperIcon />
                        <LanguagePopoverIcon />
                      </CheckoutProvider>
                    </TransactionProvider>
                  </SnackbarProvider>
                </MotionLazy>
              </ThemeProvider>
            </SettingsProvider>
          </LocalizationProvider>
        </AuthProvider>
      </ConsentProvider>
    </AppErrorBoundary>
  );
}
